export interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

function buildHeaders(token: string | null, body: BodyInit | null | undefined, extra?: HeadersInit): HeadersInit {
  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;
  return {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(extra || {}),
  };
}

async function rawFetch(endpoint: string, options: RequestInit, token: string | null): Promise<Response> {
  return fetch(`${process.env.REACT_APP_DEV_API}/${endpoint}`, {
    ...options,
    headers: buildHeaders(token, options.body, options.headers),
    credentials: "include",
  });
}

async function tryRefresh(): Promise<string | null> {
  try {
    const res = await fetch(`${process.env.REACT_APP_DEV_API}/auth/refresh`, {
      method: "POST",
      credentials: "include",
    });
    if (!res.ok) return null;
    const body = await res.json();
    const newToken = body?.data?.accessToken;
    if (!newToken) return null;
    localStorage.setItem("info", newToken);
    return newToken;
  } catch {
    return null;
  }
}

// Single-flight guard: if multiple calls hit a 401 concurrently (e.g. a page
// firing several requests on mount), they must share one in-flight refresh
// instead of each presenting the same rotating refresh cookie to the server
// independently — the server treats a reused refresh token as theft/replay
// and revokes the whole chain, which would log out a legitimate user.
// See docs/plan/03-api-and-rbac.md §3.3.
let refreshPromise: Promise<string | null> | null = null;

function getOrStartRefresh(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = tryRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// Endpoints where a 401 is a direct, legitimate outcome of the request
// itself (wrong credentials, an already-revoked session, an invalid/expired
// refresh cookie) rather than evidence that an otherwise-valid access token
// expired mid-session. Routing these through the silent-refresh flow is
// actively wrong: a failed login could be "rescued" by an unrelated refresh
// cookie left over from a previous session on the same browser, and a
// logout call can re-present an already-revoked token into reuse-detection.
const AUTH_ENDPOINTS_EXCLUDED_FROM_REFRESH = ["auth/login", "auth/logout", "auth/refresh"];

function isExcludedFromRefresh(endpoint: string): boolean {
  return AUTH_ENDPOINTS_EXCLUDED_FROM_REFRESH.some(
    (excluded) => endpoint === excluded || endpoint.startsWith(`${excluded}?`)
  );
}

// Shared by apiRequest and apiDownload so both go through the same
// single-flight 401/refresh/retry dance rather than duplicating it.
async function fetchWithAuthRetry(endpoint: string, options: RequestInit = {}, _isRetry = false): Promise<Response> {
  const token = localStorage.getItem("info");
  const response = await rawFetch(endpoint, options, token);

  if (response.status === 401 && !_isRetry && !isExcludedFromRefresh(endpoint)) {
    const newToken = await getOrStartRefresh();
    if (newToken) {
      return fetchWithAuthRetry(endpoint, options, true);
    }
    localStorage.removeItem("info");
    window.dispatchEvent(new Event("auth:logout"));
  }

  return response;
}

async function parseErrorMessage(response: Response): Promise<string> {
  let message = "Request failed";
  try {
    const body = await response.json();
    message = body.message || message;
  } catch {
    // response had no JSON body
  }
  return message;
}

export async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<ApiEnvelope<T>> {
  const response = await fetchWithAuthRetry(endpoint, options);
  if (!response.ok) throw new ApiError(await parseErrorMessage(response), response.status);
  if (response.status === 204) return undefined as unknown as ApiEnvelope<T>;
  return response.json() as Promise<ApiEnvelope<T>>;
}

export async function apiDownload(endpoint: string): Promise<{ blob: Blob; filename: string }> {
  const response = await fetchWithAuthRetry(endpoint, { method: "GET" });
  if (!response.ok) throw new ApiError(await parseErrorMessage(response), response.status);
  const disposition = response.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  return { blob: await response.blob(), filename: match ? match[1] : "download" };
}
